// =============================================================
// VARIANCE — The Executive Command Center
// File      : LeadPipeline.tsx
// Location  : /home/Applications/variance/web/src/pages/LeadPipeline.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Kanban board for managing sales leads.
// =============================================================

import { useState, useEffect } from 'react';
import { Plus, ArrowLeft, Phone, Mail, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// The exact stages from your architectural exercise
const STAGES = [
  'Intake / Inbox',
  'Attempted Contact',
  'In Discussion',
  'Nurturing',
  'Qualified',
  'Unqualified / Dead'
];

interface Lead {
  id: string;
  customer_name: string;
  project_name: string;
  status: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
}

export function LeadPipeline() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);

  const fetchLeads = () => {
    setIsLoading(true);
    fetch('/api/sales/leads')
      .then(res => res.json())
      .then(data => { if (!data.error) setLeads(data); setIsLoading(false); })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedLeadId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (!draggedLeadId) return;

    // Optimistic UI update
    setLeads(prev => prev.map(l => l.id === draggedLeadId ? { ...l, status: newStatus } : l));

    try {
      // 1. Update status
      await fetch(`/api/sales/leads/${draggedLeadId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      // 2. If Qualified, convert to Deal
      if (newStatus === 'Qualified') {
        await fetch(`/api/sales/leads/${draggedLeadId}/convert`, { method: 'POST' });
        alert('Lead Qualified! It has been moved to the Deals Pipeline.');
        fetchLeads(); // Refresh to remove it from leads
      }
    } catch (error) {
      console.error('Failed to update status', error);
      fetchLeads(); // Revert on failure
    }
    setDraggedLeadId(null);
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/sales')} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Lead Pipeline</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Drag and drop leads to update their status.</p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white hover:opacity-90 transition-all shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}>
          <Plus size={18} /> Add Lead
        </button>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 flex gap-4 overflow-x-auto pb-4">
        {isLoading ? (
          <div className="w-full flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>Loading pipeline...</div>
        ) : (
          STAGES.map(stage => {
            const stageLeads = leads.filter(l => l.status === stage);
            return (
              <div 
                key={stage} 
                className="flex-shrink-0 w-72 flex flex-col rounded-xl"
                style={{ backgroundColor: 'var(--bg-panel-alt)', border: '1px solid var(--border-subtle)' }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage)}
              >
                {/* Column Header */}
                <div className="p-3 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
                  <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{stage}</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-secondary)' }}>
                    {stageLeads.length}
                  </span>
                </div>

                {/* Column Body (Dropzone) */}
                <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[150px]">
                  {stageLeads.map(lead => (
                    <div 
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      className="p-3 rounded-lg cursor-grab active:cursor-grabbing hover:border-[var(--brand-accent)] transition-colors"
                      style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                    >
                      <div className="font-medium text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{lead.customer_name}</div>
                      <div className="flex items-center gap-1 text-xs mb-2" style={{ color: 'var(--brand-accent)' }}>
                        <Building2 size={12} /> {lead.project_name}
                      </div>
                      {lead.phone && (
                        <div className="flex items-center gap-1 text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                          <Phone size={12} /> {lead.phone}
                        </div>
                      )}
                      {lead.notes && (
                        <div className="text-xs mt-2 p-1.5 rounded bg-[var(--bg-canvas)] truncate" style={{ color: 'var(--text-muted)' }}>
                          {lead.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
